// ProcessSteps.js
import React from 'react';
import { motion } from 'framer-motion';

const ProcessSteps = () => {
  const steps = [
    {
      number: '01',
      title: 'Eligibility Consultation',
      subtitle: 'Your journey begins with an eligibility consultation.',
      points: [
        'Review your work experience and qualifications',
        'Assess role suitability and documentation readiness',
        'Explain employer requirements and timelines',
        'Provide honest feedback on your chances of progression'
      ]
    },
    {
      number: '02',
      title: 'Offer and Pipeline Placement',
      subtitle: 'Eligible candidates are:',
      points: [
        'Placed into our Work Abroad pipeline',
        'Matched with suitable roles based on profile',
        'Provided with clear next steps and expectations'
      ]
    },
    {
      number: '03',
      title: 'Interview Scheduling and Preparation',
      subtitle: 'Once in the pipeline, we:',
      points: [
        'Secure interview slots with partner employers',
        'Share interview details and timelines',
        'Provide preparation materials and guidance',
        'Support candidates with interview readiness'
      ]
    },
    {
      number: '04',
      title: 'Employer Communication and Decision',
      subtitle: 'After interviews:',
      points: [
        'Employers communicate outcomes directly to candidates',
        'Employment offers are issued directly by employers',
        'Contracts, salaries, and terms are handled between candidates and employers'
      ]
    }
  ];

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="inline-block bg-white text-[#2542D4] text-sm font-light px-6 py-2 rounded-full mb-4">
            How It Works
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Our Work Abroad Pipeline
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-2xl mx-auto">
            A clear, structured process from eligibility consultation to employment decision
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative"
            >
              <div className="grid md:grid-cols-12 gap-6 items-start">
                {/* Left Side - Number Only */}
                <div className="md:col-span-2 flex items-center md:items-start">
                  <div className="flex-shrink-0">
                    <div className="text-6xl font-light text-[#202356]/10">
                      {step.number}
                    </div>
                  </div>
                </div>

                {/* Right Side - Content */}
                <div className="md:col-span-10">
                  <div className="bg-white p-8 rounded-lg shadow-sm">
                    <h3 className="text-2xl font-semibold text-[#202356] mb-3">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm font-light mb-4">
                      {step.subtitle}
                    </p>
                    
                    {/* Points List */}
                    <ul className="space-y-2">
                      {step.points.map((point, pointIndex) => (
                        <li key={pointIndex} className="flex items-start gap-2">
                          <span className="text-[#202356]">•</span>
                          <span className="text-gray-600 text-sm font-light">{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProcessSteps;
